import {
  FocusEvent,
  FocusEventHandler,
  MouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { classNames } from "../utils/classNames";
import { Box } from "./Box";
import styles from "./Tabs.module.css";
import { Icon } from "./Icon";
import { preventDefault } from "../utils/events";
import { debounce } from "lodash";
import { Tag, TagProps } from "./Tag";
import { isTMessage, TMessage } from "../i18n/tt";

import { useDispatch, useTranslation } from "../stores/RootStoreContext";

import { DCEvent } from "../events/eventsDomain";
import { BoxProps } from "./boxHelpers";

type ScrollState = {
  scrollLeft: number;
  scrollWidth: number;
  viewportWidth: number;
};

function isOverflowLeft({ scrollLeft }: ScrollState): boolean {
  return scrollLeft > 0;
}

function isOverflowRight({ scrollLeft, scrollWidth, viewportWidth }: ScrollState): boolean {
  return scrollWidth - scrollLeft > viewportWidth;
}

function ScrollButton({
  dir,
  disabled,
  onClick,
}: {
  dir: "left" | "right";
  disabled: boolean;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={classNames("text-sm", "tw-p-2", "tw-font-bold", disabled && "tw-invisible")}
    >
      {dir === "left" ? (
        <Icon name={"caret-left"} weight={"bold"} />
      ) : (
        <Icon name={"caret-right"} weight={"bold"} />
      )}
    </button>
  );
}

function Tab({
  onSelect,
  isActive,
  onFocus,
  disabled,
  ...props
}: TabProps & {
  onFocus: (event: FocusEvent<HTMLButtonElement>) => void;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const className = classNames(
    "text-sm",
    "tw-py-3",
    "tw-px-6",
    "tw-border-b-2",
    isActive ? "border-gray-x-hard" : ["border-transparent", "subtle"],
    disabled && "tw-opacity-50",
    "tw-whitespace-nowrap",
    !disabled && "hover:bg-gray",
  );
  if ("render" in props) {
    return props.render({ className, onFocus });
  }
  return (
    <button
      className={className}
      disabled={disabled}
      onClick={preventDefault(() => {
        if (Array.isArray(onSelect)) {
          dispatch(onSelect);
        } else {
          onSelect();
        }
      })}
      onFocus={onFocus}
    >
      <Box>
        <span>{isTMessage(props.label) ? t(props.label) : props.label}</span>
        {Boolean(props.tag) &&
          (typeof props.tag === "number" || typeof props.tag === "string" ? (
            <Tag color={"success"} label={String(props.tag)} />
          ) : typeof props.tag === "object" ? (
            <Tag {...props.tag} />
          ) : undefined)}
      </Box>
    </button>
  );
}

export type TabButtonProps = {
  id: string;
  label: ReactNode | string | TMessage;
  isActive?: boolean;
  tag?: string | number | TagProps;
  onSelect: (() => void) | DCEvent[];
  disabled?: boolean;
};

export type CustomTabProps = {
  id: string;
  render: (props: { className: string; onFocus: FocusEventHandler<HTMLElement> }) => ReactElement;
  isActive?: boolean;
  onSelect: (() => void) | DCEvent[];
  disabled?: boolean;
};

export type TabProps = TabButtonProps | CustomTabProps;

export type TabsProps = {
  tabs: TabProps[];
  scrollable?: boolean;
} & BoxProps;

export function Tabs({ tabs, scrollable = true, ...props }: TabsProps) {
  const tabsKey = tabs.map((t) => t.id).join();
  const mounted = useRef(false);
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);

  const updateScrollState = useCallback(() => {
    if (mounted.current && ref) {
      setScrollLeft(ref.scrollLeft);
      setScrollWidth(ref.scrollWidth);
      setViewportWidth(ref.clientWidth);
    }
  }, [ref]);

  const debouncedUpdateScrollState = useMemo(
    () => debounce(updateScrollState, 125),
    [updateScrollState],
  );

  const handleScroll = useCallback(() => {
    debouncedUpdateScrollState();
  }, [debouncedUpdateScrollState]);

  const handleFocus = useCallback((event: FocusEvent<HTMLElement>) => {
    event.target.scrollIntoView({ inline: "nearest", block: "center", behavior: "smooth" });
  }, []);

  const handleScrollLeftClick = useCallback(() => {
    if (ref) {
      ref.scrollBy({ left: -viewportWidth, behavior: "smooth" });
    }
  }, [viewportWidth, ref]);

  const handleScrollRightClick = useCallback(() => {
    if (ref) {
      ref.scrollBy({ left: viewportWidth, behavior: "smooth" });
    }
  }, [viewportWidth, ref]);

  useEffect(() => {
    if (ref) {
      const ro = new ResizeObserver(() => {
        debouncedUpdateScrollState();
      });
      ro.observe(ref);
      return () => {
        ro.disconnect();
      };
    }
  }, [ref, debouncedUpdateScrollState]);

  useEffect(() => {
    debouncedUpdateScrollState();
  }, [tabsKey, debouncedUpdateScrollState]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return (
    <Box gap={false} {...props}>
      {scrollable && (
        <ScrollButton
          disabled={!isOverflowLeft({ scrollLeft, scrollWidth, viewportWidth })}
          dir={"left"}
          onClick={handleScrollLeftClick}
        />
      )}
      <div
        ref={setRef}
        className={classNames(
          styles.tabsScrollContainer,
          "tw-flex",
          "tw-grow",
          scrollable && "tw-overflow-x-scroll",
        )}
        tabIndex={-1}
        onScroll={handleScroll}
      >
        {tabs.map((t) => (
          <Tab {...t} key={t.id} onFocus={handleFocus} />
        ))}
      </div>
      {scrollable && (
        <ScrollButton
          disabled={!isOverflowRight({ scrollLeft, scrollWidth, viewportWidth })}
          dir={"right"}
          onClick={handleScrollRightClick}
        />
      )}
    </Box>
  );
}
